import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { firestore } from './config';
import { User, Role } from '../../types/user';

// User Profile Functions
const getUserProfile = async (userId: string) => {
  try {
    const docRef = doc(firestore, 'Users', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as User;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

export const createUserProfile = async (userData: User) => {
  try {
    // Create a document with the user's ID
    const userRef = doc(firestore, 'Users', userData.id);
    await setDoc(userRef, userData);
    return userData;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<User>) => {
  try {
    const userRef = doc(firestore, 'Users', userId);
    await updateDoc(userRef, updates);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const deleteUserProfile = async (userId: string) => {
  try {
    const userRef = doc(firestore, 'Users', userId);
    await deleteDoc(userRef);
  } catch (error) {
    console.error('Error deleting user profile:', error);
    throw error;
  }
};

// Role Management Functions
const getRole = async (roleId: string) => {
  try {
    const docRef = doc(firestore, 'Roles', roleId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Role;
    }
    return null;
  } catch (error) {
    console.error('Error fetching role:', error);
    return null;
  }
};

export const getAllRoles = async () => {
  try {
    const querySnapshot = await getDocs(collection(firestore, 'Roles'));
    const roles: Record<string, Role> = {};
    querySnapshot.forEach((doc) => {
      roles[doc.id] = {
        id: doc.id,
        ...doc.data()
      } as Role;
    });
    return roles;
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(firestore, 'Users'));
    const users: Record<string, User> = {};
    querySnapshot.forEach((doc) => {
      users[doc.id] = {
        id: doc.id,
        ...doc.data()
      } as User;
    });
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const createRole = async (role: Role) => {
  try {
    const roleRef = doc(collection(firestore, 'Roles'));
    const roleWithId = { ...role, id: roleRef.id };
    await setDoc(roleRef, roleWithId);
    return roleWithId;
  } catch (error) {
    console.error('Error creating role:', error);
    throw error;
  }
};

export const updateRole = async (roleId: string, updates: Partial<Role>) => {
  try {
    const roleRef = doc(firestore, 'Roles', roleId);
    await updateDoc(roleRef, updates);
  } catch (error) {
    console.error('Error updating role:', error);
    throw error;
  }
};

export const deleteRole = async (roleId: string) => {
  try {
    const roleRef = doc(firestore, 'Roles', roleId);
    await deleteDoc(roleRef);
  } catch (error) {
    console.error('Error deleting role:', error);
    throw error;
  }
};